import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
    AlertCircle,
    AlertTriangle,
    CheckCircle2,
    ChevronRight,
    File as FileIcon,
    Image as ImageIcon,
    Loader2,
    PauseCircle,
    Paperclip,
    Send,
    Sparkles,
    Square,
    Wrench,
    X,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    cancelSession,
    listTurns,
    openSession,
    sendMessage,
    uploadChatAttachment,
    type ChatAttachment,
    type ConstructorSession,
    type ConstructorTurn,
} from "@/api/constructor";
import { apiErrorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";

interface ScreenshotRef {
    id: string;
    url: string;
    label?: string;
}

interface Props {
    draftId: number;
    onPreviewChanged?: () => void;
    /** Optional auto-prompt to send once the session opens (e.g. upload audit). */
    autoPrompt?: string | null;
    onAutoPromptSent?: () => void;
    /** Called when the user clicks a file-creation chip in the chat. */
    onOpenFile?: (path: string) => void;
    /** Called when the user clicks a screenshot thumbnail in the chat. */
    onOpenScreenshot?: (shot: ScreenshotRef) => void;
}

export function ChatPanel({ draftId, onPreviewChanged, autoPrompt, onAutoPromptSent, onOpenFile, onOpenScreenshot }: Props): React.ReactNode {
    const queryClient = useQueryClient();
    const [session, setSession] = React.useState<ConstructorSession | null>(null);
    const [turns, setTurns] = React.useState<ConstructorTurn[]>([]);
    const [streamingText, setStreamingText] = React.useState("");
    const [running, setRunning] = React.useState(false);
    const [activeTool, setActiveTool] = React.useState<string | null>(null);
    const [runElapsedSec, setRunElapsedSec] = React.useState(0);
    /**
     * "✓ Done" markers — one per completed agent run. Positioned by
     * `afterTurnId` so they render immediately after the last turn at the
     * time the agent stopped. Carry elapsed time + tool / cost delta so
     * the operator has a positive "session ended" signal.
     */
    const [doneMarkers, setDoneMarkers] = React.useState<{ afterTurnId: number; elapsedMs: number; turnCount: number; costMicros: number; reason: "natural" | "paused" | "error"; errorMessage?: string }[]>([]);
    const turnStartRef = React.useRef<number | null>(null);
    const turnCountAtStartRef = React.useRef(0);
    const costAtStartRef = React.useRef(0);
    const [input, setInput] = React.useState("");
    const [pendingAttachments, setPendingAttachments] = React.useState<ChatAttachment[]>([]);
    const [uploadingCount, setUploadingCount] = React.useState(0);
    const [dropActive, setDropActive] = React.useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const scrollRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const s = await openSession(draftId);
                if (cancelled) return;
                setSession(s);
                const t = await listTurns(s.id);
                if (cancelled) return;
                setTurns(t);
            } catch (err) {
                toast.error(apiErrorMessage(err, "Couldn't open the AI session"));
            }
        })();
        return () => { cancelled = true; };
    }, [draftId]);

    // Keep the latest onPreviewChanged in a ref so the SSE effect below doesn't
    // depend on it. The parent passes a fresh inline closure each render, which
    // would otherwise tear down and reopen the EventSource on every tool turn —
    // dropping assistant_delta/turn/done events that arrive during the reconnect.
    const onPreviewChangedRef = React.useRef(onPreviewChanged);
    React.useEffect(() => {
        onPreviewChangedRef.current = onPreviewChanged;
    }, [onPreviewChanged]);

    React.useEffect(() => {
        if (!session) return;
        const url = `/admin/constructor/sessions/${session.id}/events`;
        const es = new EventSource(url, { withCredentials: true });
        es.addEventListener("turn", (ev) => {
            try {
                const turn = JSON.parse((ev as MessageEvent).data) as ConstructorTurn;
                setTurns((prev) => (prev.some((p) => p.id === turn.id) ? prev : [...prev, turn]));
                if (turn.role === "tool") {
                    queryClient.invalidateQueries({ queryKey: ["draft", draftId] });
                    onPreviewChangedRef.current?.();
                }
                if (turn.role === "assistant") setStreamingText("");
            } catch { /* noop */ }
        });
        es.addEventListener("assistant_delta", (ev) => {
            try {
                const { text } = JSON.parse((ev as MessageEvent).data) as { text: string };
                setStreamingText((prev) => prev + text);
            } catch { /* noop */ }
        });
        es.addEventListener("tool_call_start", (ev) => {
            try {
                const { name } = JSON.parse((ev as MessageEvent).data) as { name: string };
                setActiveTool(name);
            } catch { /* noop */ }
            setRunning(true);
        });
        es.addEventListener("tool_call_result", () => setActiveTool(null));
        es.addEventListener("done", (ev) => {
            setRunning(false);
            setStreamingText("");
            setActiveTool(null);
            let reason: "natural" | "paused" | "error" = "natural";
            let errorMessage: string | undefined;
            try {
                const parsed = JSON.parse((ev as MessageEvent).data || "{}") as { reason?: "natural" | "paused" | "error"; error?: string };
                if (parsed.reason === "paused") reason = "paused";
                if (parsed.reason === "error") { reason = "error"; errorMessage = parsed.error; }
            } catch { /* noop */ }
            openSession(draftId).then((s) => {
                setSession(s);
                const startedAt = turnStartRef.current;
                const elapsedMs = startedAt ? Math.max(0, Date.now() - startedAt) : 0;
                const totalCostMicros = s.totalCostUsdMicros || 0;
                const costDelta = Math.max(0, totalCostMicros - costAtStartRef.current);
                setTurns((prev) => {
                    const last = prev[prev.length - 1];
                    if (!last) return prev;
                    const turnDelta = prev.length - turnCountAtStartRef.current;
                    setDoneMarkers((mks) => {
                        if (mks.some((m) => m.afterTurnId === last.id)) return mks;
                        return [...mks, { afterTurnId: last.id, elapsedMs, turnCount: turnDelta, costMicros: costDelta, reason, errorMessage }];
                    });
                    return prev;
                });
                turnStartRef.current = null;
            }).catch(() => {});
        });
        return () => es.close();
    }, [session, draftId, queryClient]);

    React.useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [turns, streamingText]);

    /**
     * Reconstruct "✓ Done" / "⚠ Crashed" / "⏸ Paused" markers from persisted
     * turn history so a session that the operator reopens looks the same as
     * one they kept open while it ran. Runtime markers (set in the SSE "done"
     * handler) take priority; derived ones fill the gaps.
     *
     * A "run" is the slice between consecutive user turns (last run runs to
     * end of list). The reason is inferred from the last assistant message
     * — agents have a stable phrasing for crashes ("⚠ Agent run failed…") and
     * pauses ("send a message to continue…").
     */
    const derivedDoneMarkers = React.useMemo(() => {
        type Marker = { afterTurnId: number; elapsedMs: number; turnCount: number; costMicros: number; reason: "natural" | "paused" | "error"; errorMessage?: string };
        if (turns.length === 0) return [] as Marker[];
        const out: Marker[] = [];
        const closeRun = (startIdx: number, endIdx: number) => {
            if (endIdx <= startIdx) return;
            const start = turns[startIdx];
            const end = turns[endIdx];
            let lastAssistant = "";
            for (let i = endIdx; i > startIdx; i--) {
                if (turns[i].role === "assistant" && turns[i].content) {
                    lastAssistant = turns[i].content!;
                    break;
                }
            }
            const startMs = Date.parse(start.createdAt);
            const endMs = Date.parse(end.createdAt);
            const elapsedMs = Number.isFinite(startMs) && Number.isFinite(endMs)
                ? Math.max(0, endMs - startMs) : 0;
            let reason: "natural" | "paused" | "error" = "natural";
            let errorMessage: string | undefined;
            if (/agent run (failed|crashed)|exceeded.{0,40}deadline|⚠/i.test(lastAssistant)) {
                reason = "error";
                const m = lastAssistant.match(/(?:failed|crashed|error)[:\s]+([^\n]+)/i);
                if (m) errorMessage = m[1].slice(0, 200);
            } else if (/paused|stopped|cancelled|send.{0,20}(?:message|reply).{0,20}(?:continue|retry)/i.test(lastAssistant)) {
                reason = "paused";
            }
            out.push({ afterTurnId: end.id, elapsedMs, turnCount: endIdx - startIdx, costMicros: 0, reason, errorMessage });
        };
        let runStart = -1;
        for (let i = 0; i < turns.length; i++) {
            if (turns[i].role === "user") {
                if (runStart >= 0) closeRun(runStart, i - 1);
                runStart = i;
            }
        }
        // Trailing run: only synthesise when the session isn't currently running
        // (otherwise the SSE handler will deliver the real marker shortly).
        if (!running && runStart >= 0) closeRun(runStart, turns.length - 1);
        return out;
    }, [turns, running]);

    /** Live (SSE-delivered) markers override anything we derived from history. */
    const effectiveDoneMarkers = React.useMemo(() => {
        const live = new Set(doneMarkers.map((m) => m.afterTurnId));
        const merged = [...doneMarkers];
        for (const m of derivedDoneMarkers) if (!live.has(m.afterTurnId)) merged.push(m);
        return merged;
    }, [doneMarkers, derivedDoneMarkers]);

    // Tick a per-second elapsed counter while running, so the busy banner
    // shows visible motion and the operator can judge whether to keep
    // waiting or hit Stop.
    React.useEffect(() => {
        if (!running) { setRunElapsedSec(0); return; }
        const startedAt = turnStartRef.current ?? Date.now();
        const id = window.setInterval(() => {
            setRunElapsedSec(Math.floor((Date.now() - startedAt) / 1000));
        }, 1000);
        return () => window.clearInterval(id);
    }, [running]);

    const cancelMut = useMutation({
        mutationFn: () => cancelSession(session!.id),
        onSuccess: (r) => {
            if (r.stopped) toast.success("Agent stopped");
            // SSE "done" with reason:"cancelled" will fire and reset UI.
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Couldn't stop the agent")),
    });

    const sendMut = useMutation({
        mutationFn: ({ content, attachmentIds }: { content: string; attachmentIds: number[] }) =>
            sendMessage(session!.id, content, attachmentIds),
        onMutate: ({ content }: { content: string; attachmentIds: number[] }) => {
            // Capture the per-run baseline so the "✓ Done" marker can show
            // elapsed time + turn / cost delta for THIS run only.
            turnStartRef.current = Date.now();
            turnCountAtStartRef.current = turns.length;
            costAtStartRef.current = session?.totalCostUsdMicros ?? 0;
            setRunning(true);
            return { content };
        },
        onError: (err, _vars, ctx) => {
            setRunning(false);
            // 409 from the server = agent busy. Restore the user's text so
            // they don't lose it, and tell them to wait — don't pretend we
            // sent it.
            const status = (err as { response?: { status?: number } })?.response?.status;
            if (status === 409 && ctx && typeof ctx === "object" && "content" in ctx) {
                const restored = ((ctx as { content: string }).content || "").trim();
                if (restored) setInput((cur) => cur ? cur : restored);
                toast.error("Agent is still processing your previous message — wait for it to finish.");
                return;
            }
            toast.error(apiErrorMessage(err, "Send failed"));
        },
    });

    // Auto-prompt: send once the session is open and there are no prior turns.
    React.useEffect(() => {
        if (!session || !autoPrompt) return;
        if (turns.length > 0) return;
        sendMut.mutate({ content: autoPrompt, attachmentIds: [] });
        onAutoPromptSent?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session, autoPrompt]);

    const onSend = () => {
        const text = input.trim();
        if ((!text && pendingAttachments.length === 0) || !session) return;
        const attachmentIds = pendingAttachments.map((a) => a.id);
        setInput("");
        setPendingAttachments([]);
        sendMut.mutate({ content: text, attachmentIds });
    };

    const uploadFiles = React.useCallback(async (files: FileList | File[] | null) => {
        if (!session || !files || files.length === 0) return;
        const arr = Array.from(files);
        setUploadingCount((c) => c + arr.length);
        for (const file of arr) {
            try {
                const att = await uploadChatAttachment(session.id, file);
                setPendingAttachments((prev) => [...prev, att]);
            } catch (err) {
                toast.error(apiErrorMessage(err, `Upload failed: ${file.name}`));
            } finally {
                setUploadingCount((c) => Math.max(0, c - 1));
            }
        }
    }, [session]);

    const onPaste = React.useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        if (!e.clipboardData?.files || e.clipboardData.files.length === 0) return;
        e.preventDefault();
        void uploadFiles(e.clipboardData.files);
    }, [uploadFiles]);

    const onDrop = React.useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setDropActive(false);
        if (!e.dataTransfer?.files) return;
        void uploadFiles(e.dataTransfer.files);
    }, [uploadFiles]);

    const removePending = (id: number) => {
        setPendingAttachments((prev) => prev.filter((a) => a.id !== id));
    };

    const totalCost = session?.totalCostUsdMicros ?? 0;

    return (
        <div className="flex h-full w-full min-w-0 flex-col border-l border-border bg-background">
            <div className="flex min-w-0 items-center gap-2 border-b border-border px-4 py-2">
                <Sparkles className="h-4 w-4 shrink-0 text-primary" />
                <span className="truncate text-sm font-medium">AI assistant</span>
                {session && (
                    <span className="ml-auto truncate font-mono text-[10px] uppercase tracking-wider text-muted-foreground" data-testid="cost-meter">
                        {session.model} · {formatCost(totalCost)}
                    </span>
                )}
            </div>

            <div ref={scrollRef} className="flex min-w-0 flex-1 flex-col gap-3 overflow-x-hidden overflow-y-auto p-4">
                {turns.length === 0 && !streamingText && (
                    <div className="space-y-3">
                        <div className="rounded-md bg-muted/30 p-4 text-sm text-muted-foreground">
                            Describe what you want to build. The AI can read/write files, generate
                            images, render the page in a real browser to find errors, and edit
                            existing markup. Press <kbd className="rounded border px-1 text-xs">⌘ Enter</kbd> to send.
                        </div>
                        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Starter prompts</div>
                        <div className="flex flex-wrap gap-1.5">
                            {STARTERS.map((s) => (
                                <button
                                    key={s.label}
                                    type="button"
                                    onClick={() => setInput(s.prompt)}
                                    className="rounded-full border border-border bg-background px-3 py-1 text-xs hover:bg-muted"
                                    data-testid={`starter-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                <GroupedTurns
                    turns={turns}
                    draftId={draftId}
                    onOpenFile={onOpenFile}
                    onOpenScreenshot={onOpenScreenshot}
                    doneMarkers={effectiveDoneMarkers}
                />
                {streamingText && <StreamingAssistantBubble text={streamingText} />}
                {running && !streamingText && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="ai-thinking">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        {activeTool ? `running ${activeTool}…` : "thinking…"}
                    </div>
                )}
            </div>

            {running && (
                <BusyBanner
                    activeTool={activeTool}
                    elapsedSec={runElapsedSec}
                    onStop={() => cancelMut.mutate()}
                    stopping={cancelMut.isPending}
                />
            )}

            <div
                className={cn(
                    "relative border-t border-border p-3",
                    dropActive && "bg-primary/5 outline outline-2 outline-primary/40 -outline-offset-4",
                    running && "bg-muted/30",
                )}
                onDragOver={(e) => { e.preventDefault(); setDropActive(true); }}
                onDragLeave={() => setDropActive(false)}
                onDrop={onDrop}
            >
                {dropActive && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs text-primary">
                        drop to attach
                    </div>
                )}
                {pendingAttachments.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-1.5">
                        {pendingAttachments.map((a) => <PendingAttachmentChip key={a.id} att={a} onRemove={() => removePending(a.id)} />)}
                    </div>
                )}
                <Textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                        // ⌘ Enter sends ONLY when the agent is idle. While running
                        // the same shortcut is silently ignored so the operator
                        // doesn't accidentally queue work that will 409.
                        if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && !running) {
                            e.preventDefault();
                            onSend();
                        }
                    }}
                    onPaste={onPaste}
                    placeholder={running
                        ? "Agent is working — hit Stop to interrupt, or queue your next thought here…"
                        : pendingAttachments.length > 0
                            ? "Tell the AI what to do with the attached file(s)…"
                            : "Describe what you want to build or change… (drop, paste, or pick files to attach)"}
                    rows={3}
                    className="resize-none text-sm"
                    // Composer stays editable while running so the operator can
                    // draft their next message — only Send is gated.
                    disabled={!session}
                    data-testid="chat-composer"
                />
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => { void uploadFiles(e.target.files); e.target.value = ""; }}
                    data-testid="chat-attach-input"
                />
                <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-7 gap-1 px-2 text-xs"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={!session || running}
                            data-testid="chat-attach-button"
                            title={running ? "Agent is working — wait or hit Stop" : "Attach files (or drag/drop, paste)"}
                        >
                            <Paperclip className="h-3.5 w-3.5" />
                            Attach
                        </Button>
                        {uploadingCount > 0 && (
                            <span className="text-[10px] text-muted-foreground">
                                <Loader2 className="inline h-3 w-3 animate-spin" /> uploading {uploadingCount}…
                            </span>
                        )}
                        {!running && (
                            <span className="text-[10px] text-muted-foreground">⌘+Enter to send</span>
                        )}
                    </div>
                    {running ? (
                        <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            onClick={() => cancelMut.mutate()}
                            disabled={!session || cancelMut.isPending}
                            data-testid="chat-stop"
                            title="Cancel the in-flight agent run"
                        >
                            <Square className="h-3.5 w-3.5 fill-current" />
                            {cancelMut.isPending ? "Stopping…" : "Stop"}
                        </Button>
                    ) : (
                        <Button
                            type="button"
                            size="sm"
                            onClick={onSend}
                            disabled={!session || (!input.trim() && pendingAttachments.length === 0) || uploadingCount > 0}
                            data-testid="chat-send"
                        >
                            <Send className="h-3.5 w-3.5" />
                            Send
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}

function BusyBanner({ activeTool, elapsedSec, onStop, stopping }: {
    activeTool: string | null;
    elapsedSec: number;
    onStop: () => void;
    stopping: boolean;
}): React.ReactNode {
    const mins = Math.floor(elapsedSec / 60);
    const secs = elapsedSec % 60;
    const elapsed = mins > 0 ? `${mins}:${String(secs).padStart(2, "0")}` : `${secs}s`;
    const label = activeTool ? `running ${activeTool}` : "thinking";
    return (
        <div className="flex items-center gap-2 border-t border-amber-300/50 bg-amber-50 px-3 py-2 dark:bg-amber-950/30">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-700 dark:text-amber-300" />
            <div className="flex-1 text-xs text-amber-900 dark:text-amber-100">
                <span className="font-medium">Agent working</span>
                <span className="text-amber-700/80 dark:text-amber-300/80"> · {label} · {elapsed}</span>
            </div>
            <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={onStop}
                disabled={stopping}
                className="h-7 gap-1 px-2 text-xs text-amber-900 hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-900/40"
                data-testid="busy-banner-stop"
            >
                <Square className="h-3 w-3 fill-current" />
                {stopping ? "Stopping…" : "Stop"}
            </Button>
        </div>
    );
}

function formatCost(micros: number): string {
    const usd = micros / 1_000_000;
    if (usd === 0) return "$0.00";
    if (usd >= 1) return `$${usd.toFixed(2)}`;
    if (usd >= 0.01) return `$${usd.toFixed(3)}`;
    if (usd >= 0.0001) return `$${usd.toFixed(4)}`;
    return "<$0.0001";
}

interface CardCallbacks {
    onOpenFile?: (path: string) => void;
    onOpenScreenshot?: (shot: ScreenshotRef) => void;
    draftId: number;
}

/** "alice@example.com" → "alice"; falls back to the original on shapes like "noreply". */
function shortAuthor(email: string): string {
    const at = email.indexOf("@");
    return at > 0 ? email.slice(0, at) : email;
}

function DoneMarker({ marker }: { marker: { elapsedMs: number; turnCount: number; costMicros: number; reason: "natural" | "paused" | "error"; errorMessage?: string } }): React.ReactNode {
    const elapsed = formatElapsed(marker.elapsedMs);
    const cost = formatCost(marker.costMicros);
    const stats = `· ${marker.turnCount} turns · ${elapsed} · ${cost}`;
    if (marker.reason === "error") {
        return (
            <div className="my-1 flex items-start gap-2">
                <div className="h-px flex-1 bg-destructive/40 mt-2" />
                <div className="flex items-start gap-1.5 rounded-md border border-destructive/60 bg-destructive/5 px-2 py-1 text-[10px] uppercase tracking-wider text-destructive">
                    <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                    <div className="flex flex-col">
                        <span>Agent crashed — send a message to retry</span>
                        {marker.errorMessage && (
                            <span className="text-muted-foreground/80 normal-case tracking-normal break-all max-w-[40ch]">{marker.errorMessage}</span>
                        )}
                        <span className="text-muted-foreground/70 normal-case tracking-normal">{stats}</span>
                    </div>
                </div>
                <div className="h-px flex-1 bg-destructive/40 mt-2" />
            </div>
        );
    }
    const isPaused = marker.reason === "paused";
    return (
        <div className="my-1 flex items-center gap-2">
            <div className={cn("h-px flex-1", isPaused ? "bg-amber-300/50" : "bg-emerald-300/50")} />
            {isPaused ? (
                <div className="flex items-center gap-1.5 rounded-full border border-amber-300/60 bg-amber-50 px-2 py-0.5 text-[10px] uppercase tracking-wider text-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
                    <PauseCircle className="h-3 w-3" />
                    <span>Paused — send a message to continue</span>
                    <span className="text-muted-foreground/70 normal-case tracking-normal">{stats}</span>
                </div>
            ) : (
                <div className="flex items-center gap-1.5 rounded-full border border-emerald-300/60 bg-emerald-50 px-2 py-0.5 text-[10px] uppercase tracking-wider text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-200">
                    <CheckCircle2 className="h-3 w-3" />
                    <span>Done</span>
                    <span className="text-muted-foreground/70 normal-case tracking-normal">{stats}</span>
                </div>
            )}
            <div className={cn("h-px flex-1", isPaused ? "bg-amber-300/50" : "bg-emerald-300/50")} />
        </div>
    );
}

function formatElapsed(ms: number): string {
    if (!ms) return "0s";
    const sec = Math.round(ms / 1000);
    if (sec < 60) return `${sec}s`;
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return s ? `${m}m ${s}s` : `${m}m`;
}

function PendingAttachmentChip({ att, onRemove }: { att: ChatAttachment; onRemove: () => void }): React.ReactNode {
    const isImage = att.mime.startsWith("image/");
    return (
        <div className="flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-1.5 py-1 pr-2 text-xs">
            {isImage ? (
                <img src={att.url} alt={att.originalName} className="h-8 w-8 rounded object-cover" />
            ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded bg-muted">
                    <FileIcon className="h-4 w-4 text-muted-foreground" />
                </div>
            )}
            <span className="max-w-[140px] truncate" title={att.originalName}>{att.originalName}</span>
            <button
                type="button"
                onClick={onRemove}
                className="ml-1 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label={`Remove ${att.originalName}`}
            >
                <X className="h-3 w-3" />
            </button>
        </div>
    );
}

function AttachmentThumb({ att, onOpenScreenshot }: { att: ChatAttachment; onOpenScreenshot?: (s: ScreenshotRef) => void }): React.ReactNode {
    const isImage = att.mime.startsWith("image/");
    if (isImage) {
        return (
            <button
                type="button"
                onClick={() => onOpenScreenshot?.({ id: `att-${att.id}`, url: att.url, label: att.originalName })}
                className="overflow-hidden rounded-md border border-border bg-muted/30 transition-opacity hover:opacity-80"
                title={`${att.originalName} (${Math.round(att.sizeBytes / 1024)}kb)`}
            >
                <img src={att.url} alt={att.originalName} className="max-h-32 max-w-[200px] object-contain" />
            </button>
        );
    }
    return (
        <a
            href={att.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-w-0 max-w-full items-center gap-1.5 overflow-hidden rounded-md border border-border bg-muted/30 px-2 py-1 text-xs hover:bg-muted"
            title={att.mime}
        >
            <FileIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="min-w-0 truncate">{att.originalName}</span>
            <span className="shrink-0 text-muted-foreground">· {Math.round(att.sizeBytes / 1024)}kb</span>
        </a>
    );
}

/**
 * Renders the chronological chat by collapsing consecutive tool / empty-
 * assistant turns into a single "agent worked: N steps" group.
 *
 * Why: the agent typically emits text → 10–30 silent tool calls → text →
 * more tool calls. During a live session the operator sees the streaming
 * bubble narrate between tool calls so it reads well. On reopen those
 * streaming bubbles are gone (they were never persisted) and the chat
 * becomes a wall of identical small cards stacked with no rhythm — the
 * "looks like shit" complaint. Grouping restores the narrative: the
 * operator scans assistant messages + grouped thought-blocks, and can
 * expand any block to see the underlying tool detail.
 */
function GroupedTurns({ turns, draftId, onOpenFile, onOpenScreenshot, doneMarkers }: {
    turns: ConstructorTurn[];
    draftId: number;
    onOpenFile?: (path: string) => void;
    onOpenScreenshot?: (s: ScreenshotRef) => void;
    doneMarkers: { afterTurnId: number; elapsedMs: number; turnCount: number; costMicros: number; reason: "natural" | "paused" | "error"; errorMessage?: string }[];
}): React.ReactNode {
    type Block =
        | { kind: "user"; turn: ConstructorTurn; key: string }
        | { kind: "assistant"; turn: ConstructorTurn; key: string }
        | { kind: "tools"; turns: ConstructorTurn[]; key: string };

    const blocks = React.useMemo<Block[]>(() => {
        const out: Block[] = [];
        let toolBuf: ConstructorTurn[] = [];
        const flushTools = () => {
            if (toolBuf.length === 0) return;
            out.push({ kind: "tools", turns: toolBuf, key: `tools-${toolBuf[0].id}-${toolBuf[toolBuf.length - 1].id}` });
            toolBuf = [];
        };
        for (const t of turns) {
            if (t.role === "user") {
                flushTools();
                out.push({ kind: "user", turn: t, key: `u-${t.id}` });
            } else if (t.role === "assistant") {
                if (t.content && t.content.trim()) {
                    flushTools();
                    out.push({ kind: "assistant", turn: t, key: `a-${t.id}` });
                }
                // empty assistant turns are silent (they only carry tool_calls)
            } else if (t.role === "tool") {
                toolBuf.push(t);
            }
        }
        flushTools();
        return out;
    }, [turns]);

    const lastTurnIdOfBlock = (b: Block): number => {
        if (b.kind === "tools") return b.turns[b.turns.length - 1].id;
        return b.turn.id;
    };

    return (
        <>
            {blocks.map((b) => {
                const markers = doneMarkers.filter((m) => m.afterTurnId === lastTurnIdOfBlock(b));
                let body: React.ReactNode = null;
                if (b.kind === "user") {
                    body = <TurnView turn={b.turn} draftId={draftId} onOpenFile={onOpenFile} onOpenScreenshot={onOpenScreenshot} />;
                } else if (b.kind === "assistant") {
                    body = <TurnView turn={b.turn} draftId={draftId} onOpenFile={onOpenFile} onOpenScreenshot={onOpenScreenshot} />;
                } else {
                    body = <ToolBlock turns={b.turns} draftId={draftId} onOpenFile={onOpenFile} onOpenScreenshot={onOpenScreenshot} />;
                }
                return (
                    <React.Fragment key={b.key}>
                        {body}
                        {markers.map((m, i) => (
                            <DoneMarker key={`done-${b.key}-${i}`} marker={m} />
                        ))}
                    </React.Fragment>
                );
            })}
        </>
    );
}

/**
 * Collapsible group of consecutive tool calls. Collapsed by default if there
 * are more than 3 tools in the group — single-step thoughts render inline
 * exactly like the old un-grouped behaviour.
 */
function ToolBlock({ turns, draftId, onOpenFile, onOpenScreenshot }: {
    turns: ConstructorTurn[];
    draftId: number;
    onOpenFile?: (path: string) => void;
    onOpenScreenshot?: (s: ScreenshotRef) => void;
}): React.ReactNode {
    // ALWAYS render every tool turn — the operator wants full chronological
    // history on reopen. The block wrapper exists only to give consecutive
    // tool runs a subtle visual envelope (left border + rollup header) so
    // they don't merge into an undifferentiated stripe stack, and to offer
    // an optional collapse for very long thought-blocks. Default state =
    // expanded.
    const [open, setOpen] = React.useState(true);

    if (turns.length <= 3) {
        // Short runs: just stack the cards. No wrapper, no rollup header.
        return (
            <div className="flex min-w-0 flex-col gap-1.5">
                {turns.map((t) => (
                    <ToolCallCard key={t.id} turn={t} draftId={draftId}
                        onOpenFile={onOpenFile} onOpenScreenshot={onOpenScreenshot} />
                ))}
            </div>
        );
    }

    // Longer runs: subtle wrapper + rollup header (clickable to collapse if
    // the operator wants to). Default = open so every card is visible on
    // first paint.
    const counts = new Map<string, number>();
    let errCount = 0, warnCount = 0;
    for (const t of turns) {
        const n = t.toolName || "tool";
        counts.set(n, (counts.get(n) || 0) + 1);
        const parsed = safeParse(t.toolResult);
        const status = computeStatus(t.toolName, parsed);
        if (status === "error") errCount++;
        else if (status === "warn") warnCount++;
    }
    const namesSummary = [...counts.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 4)
        .map(([n, c]) => c > 1 ? `${n}×${c}` : n)
        .join(", ");
    const moreNames = counts.size > 4 ? `, +${counts.size - 4} more` : "";
    return (
        <div className={cn(
            "min-w-0 overflow-hidden rounded-md border bg-muted/10",
            errCount > 0 && "border-destructive/40",
            errCount === 0 && warnCount > 0 && "border-amber-400/40",
        )}>
            <button
                type="button"
                className="flex w-full min-w-0 items-center gap-2 px-3 py-1.5 text-left text-[11px] hover:bg-muted/40"
                onClick={() => setOpen((x) => !x)}
                data-testid={`tool-block-${turns[0].id}-${turns[turns.length - 1].id}`}
                title={open ? "Collapse" : "Expand"}
            >
                <ChevronRight className={cn("h-3 w-3 shrink-0 transition-transform", open && "rotate-90")} />
                <Wrench className="h-3 w-3 shrink-0 text-muted-foreground" />
                <span className="shrink-0 text-muted-foreground">Agent worked:</span>
                <span className="min-w-0 truncate font-mono">{namesSummary}{moreNames}</span>
                <span className="ml-auto flex shrink-0 items-center gap-1.5">
                    {errCount > 0 && <Chip color="error">{errCount} err</Chip>}
                    {warnCount > 0 && <Chip color="warn">{warnCount} warn</Chip>}
                    <Chip color="ok">{turns.length} steps</Chip>
                </span>
            </button>
            {open && (
                <div className="flex flex-col gap-1.5 border-t border-border/60 p-2">
                    {turns.map((t) => (
                        <ToolCallCard key={t.id} turn={t} draftId={draftId}
                            onOpenFile={onOpenFile} onOpenScreenshot={onOpenScreenshot} />
                    ))}
                </div>
            )}
        </div>
    );
}

function TurnView({ turn, draftId, onOpenFile, onOpenScreenshot }: { turn: ConstructorTurn } & CardCallbacks): React.ReactNode {
    if (turn.role === "user") {
        // Per-page chat is shared across admins — show who wrote each message.
        const author = turn.authorEmail ? shortAuthor(turn.authorEmail) : null;
        const atts = turn.attachments || [];
        return (
            <div className="flex min-w-0 flex-col items-end gap-1">
                {author && (
                    <div className="text-[10px] text-muted-foreground/80 pr-1" title={turn.authorEmail ?? undefined}>
                        {author}
                    </div>
                )}
                {atts.length > 0 && (
                    <div className="flex max-w-[85%] flex-wrap justify-end gap-1.5">
                        {atts.map((a) => <AttachmentThumb key={a.id} att={a} onOpenScreenshot={onOpenScreenshot} />)}
                    </div>
                )}
                {turn.content && (
                    <div className="max-w-[85%] overflow-hidden whitespace-pre-wrap break-words rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground">
                        {turn.content}
                    </div>
                )}
            </div>
        );
    }
    if (turn.role === "assistant") {
        if (!turn.content) return null;
        return <AssistantBubble text={turn.content} />;
    }
    if (turn.role === "tool") return <ToolCallCard turn={turn} draftId={draftId} onOpenFile={onOpenFile} onOpenScreenshot={onOpenScreenshot} />;
    return null;
}

// Tailwind typography lets <pre>/<code>/<a> overflow horizontally by default.
// In a fixed-width chat column that turns into "text gliding past the right
// border" the moment a code block or long URL shows up. Force wrapping at
// every level: words break inside the body, code blocks wrap, long links
// soft-break. Anything that wants horizontal scroll lives inside a tool
// card, not in the assistant bubble.
const ASSISTANT_BUBBLE_CLASSES = "prose prose-sm w-full min-w-0 max-w-full overflow-hidden rounded-lg bg-muted/50 px-3 py-2 dark:prose-invert "
    + "[overflow-wrap:anywhere] "
    + "prose-p:break-words prose-li:break-words prose-headings:break-words "
    + "prose-pre:max-w-full prose-pre:whitespace-pre-wrap prose-pre:break-words "
    + "prose-code:break-words prose-a:break-all";

function AssistantBubble({ text }: { text: string }): React.ReactNode {
    return (
        <div className={ASSISTANT_BUBBLE_CLASSES}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
        </div>
    );
}

function StreamingAssistantBubble({ text }: { text: string }): React.ReactNode {
    return (
        <div className={ASSISTANT_BUBBLE_CLASSES} data-testid="streaming-bubble">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
            <span className="inline-block h-3 w-1.5 animate-pulse bg-primary align-middle" />
        </div>
    );
}

function ToolCallCard({ turn, draftId, onOpenFile, onOpenScreenshot }: { turn: ConstructorTurn } & CardCallbacks): React.ReactNode {
    const [open, setOpen] = React.useState(false);
    const parsedArgs = safeParse(turn.toolArgs);
    const parsedResult = safeParse(turn.toolResult);
    const status = computeStatus(turn.toolName, parsedResult);
    const fileMarks = extractFileMarks(turn.toolName, parsedArgs, parsedResult);
    const shotRefs = extractScreenshotRefs(turn.toolName, parsedResult, draftId);

    return (
        <div
            className={cn(
                "w-full min-w-0 max-w-full overflow-hidden rounded-md border bg-card text-xs",
                status === "error" && "border-destructive/40 bg-destructive/5",
                status === "warn" && "border-amber-400/40 bg-amber-50 dark:bg-amber-950/20",
                status === "ok" && "border-border",
            )}
            data-testid={`tool-card-${turn.toolName}`}
        >
            <button
                type="button"
                className="flex w-full min-w-0 items-center gap-2 px-2.5 py-1.5 text-left hover:bg-muted/40"
                onClick={() => setOpen((x) => !x)}
            >
                <ChevronRight className={cn("h-3 w-3 shrink-0 transition-transform", open && "rotate-90")} />
                <Wrench className="h-3 w-3 shrink-0 text-muted-foreground" />
                <span className="truncate font-mono">{turn.toolName}</span>
                <span className="ml-auto flex min-w-0 shrink items-center gap-1.5 overflow-hidden">
                    <StructuredSummary name={turn.toolName} result={parsedResult} />
                </span>
            </button>
            {/* Chronological "marks" — clickable file chips + screenshot thumbnails
                that link the chat to the underlying resources. */}
            {(fileMarks.length > 0 || shotRefs.length > 0) && (
                <div className="flex min-w-0 flex-wrap items-center gap-1.5 border-t border-border/60 px-2.5 py-1.5">
                    {fileMarks.map((m, i) => (
                        <FileChip key={`f-${i}`} mark={m} onOpenFile={onOpenFile} />
                    ))}
                    {shotRefs.map((s) => (
                        <ScreenshotChip key={s.id} shot={s} onOpenScreenshot={onOpenScreenshot} />
                    ))}
                </div>
            )}
            {open && (
                <div className="min-w-0 max-w-full overflow-hidden border-t border-border bg-muted/20 p-2"
                     data-testid="tool-card-body">
                    <StructuredView name={turn.toolName} args={parsedArgs} result={parsedResult} />
                </div>
            )}
        </div>
    );
}

interface FileMark {
    kind: "write" | "edit" | "delete" | "move" | "asset" | "image";
    path: string;
    detail?: string;
}

/** Walk a tool turn's args/result and pull out file mutations the chat
 *  should mark for the operator. */
function extractFileMarks(name: string | null, args: unknown, result: unknown): FileMark[] {
    const r = (result && typeof result === "object") ? (result as Record<string, unknown>) : null;
    const a = (args && typeof args === "object") ? (args as Record<string, unknown>) : null;
    const out: FileMark[] = [];
    if (!r) return out;
    if ((name === "write_file" || name === "fork_from_url") && r.path) {
        out.push({ kind: "write", path: String(r.path), detail: r.sizeBytes ? `${r.sizeBytes}b` : undefined });
    }
    if (name === "edit_file" && r.path) {
        out.push({ kind: "edit", path: String(r.path), detail: r.replacements ? `${r.replacements} edits` : undefined });
    }
    if (name === "delete_file" && a && a.path) {
        out.push({ kind: "delete", path: String(a.path) });
    }
    if (name === "move_file" && a && a.fromPath && a.toPath) {
        out.push({ kind: "move", path: String(a.toPath), detail: `from ${a.fromPath}` });
    }
    if (name === "upload_asset_from_url" && r.path) {
        out.push({ kind: "asset", path: String(r.path), detail: r.bytes ? `${r.bytes}b` : undefined });
    }
    if (name === "generate_image" && r.path) {
        const dim = r.width && r.height ? `${r.width}×${r.height}` : undefined;
        out.push({ kind: "image", path: String(r.path), detail: dim });
    }
    if (name === "fork_from_url" && Array.isArray(r.images)) {
        for (const img of r.images as { draftPath?: string; bytes?: number }[]) {
            if (img.draftPath) {
                out.push({ kind: "asset", path: img.draftPath, detail: img.bytes ? `${img.bytes}b` : undefined });
            }
        }
    }
    return out;
}

/** Walk a tool turn's result for screenshot refs the chat should preview. */
function extractScreenshotRefs(name: string | null, result: unknown, draftId: number): ScreenshotRef[] {
    if (!result || typeof result !== "object") return [];
    const r = result as Record<string, unknown>;
    const refs: ScreenshotRef[] = [];
    const collect = (entries: unknown[], fallbackLabel: string) => {
        for (const e of entries) {
            if (!e || typeof e !== "object") continue;
            const id = (e as Record<string, unknown>).id;
            const label = (e as Record<string, unknown>).label;
            if (typeof id === "string") {
                refs.push({
                    id,
                    label: typeof label === "string" ? label : fallbackLabel,
                    url: `/admin/constructor/drafts/${draftId}/screenshots/${id}`,
                });
            }
        }
    };
    // browse_url / view_url return result.screenshots[]
    if (Array.isArray(r.screenshots)) collect(r.screenshots as unknown[], name || "shot");
    // failed branch nests screenshot ref
    const failed = r.failed;
    if (failed && typeof failed === "object") {
        const fs = (failed as Record<string, unknown>).screenshot;
        if (fs && typeof fs === "object" && typeof (fs as Record<string, unknown>).id === "string") {
            refs.push({
                id: String((fs as Record<string, unknown>).id),
                label: String((fs as Record<string, unknown>).label ?? "pre-failure"),
                url: `/admin/constructor/drafts/${draftId}/screenshots/${(fs as Record<string, unknown>).id}`,
            });
        }
    }
    return refs;
}

function FileChip({ mark, onOpenFile }: { mark: FileMark; onOpenFile?: (path: string) => void }): React.ReactNode {
    const verb = mark.kind === "edit" ? "Edited"
               : mark.kind === "delete" ? "Deleted"
               : mark.kind === "move" ? "Renamed"
               : mark.kind === "asset" ? "Uploaded"
               : mark.kind === "image" ? "Generated"
               : "Wrote";
    const clickable = mark.kind !== "delete" && !!onOpenFile;
    return (
        <button
            type="button"
            disabled={!clickable}
            onClick={() => onOpenFile && onOpenFile(mark.path)}
            className={cn(
                "inline-flex min-w-0 max-w-full items-center gap-1.5 overflow-hidden rounded-md border bg-card px-2 py-0.5 text-[10px]",
                clickable && "hover:bg-muted cursor-pointer",
                !clickable && "opacity-70",
            )}
            title={mark.detail ? `${verb} ${mark.path} (${mark.detail})` : `${verb} ${mark.path}`}
            data-testid={`file-mark-${mark.path}`}
        >
            <span className="shrink-0 text-muted-foreground">{verb}</span>
            <span className="min-w-0 truncate font-mono">{mark.path}</span>
            {mark.detail && <span className="shrink-0 text-muted-foreground">{mark.detail}</span>}
        </button>
    );
}

function ScreenshotChip({ shot, onOpenScreenshot }: { shot: ScreenshotRef; onOpenScreenshot?: (s: ScreenshotRef) => void }): React.ReactNode {
    return (
        <button
            type="button"
            onClick={() => onOpenScreenshot && onOpenScreenshot(shot)}
            className="inline-flex min-w-0 max-w-full items-center gap-1.5 overflow-hidden rounded-md border bg-card px-2 py-0.5 text-[10px] hover:bg-muted"
            title={shot.label ? `Screenshot · ${shot.label}` : `Screenshot ${shot.id}`}
            data-testid={`shot-mark-${shot.id}`}
        >
            <ImageIcon className="h-3 w-3 shrink-0 text-muted-foreground" />
            <span className="min-w-0 truncate font-mono">{shot.label || shot.id.slice(-8)}</span>
        </button>
    );
}

function safeParse(s: string | null): unknown {
    if (!s) return null;
    try { return JSON.parse(s); } catch { return s; }
}

function computeStatus(name: string | null, result: unknown): "ok" | "warn" | "error" {
    if (!result || typeof result !== "object") return "ok";
    const r = result as Record<string, unknown>;
    if (r.error) return "error";
    if (name === "render_page" && r.summary && typeof r.summary === "object") {
        const s = r.summary as Record<string, number>;
        if ((s.consoleErrorCount ?? 0) + (s.pageErrorCount ?? 0) + (s.brokenAssetCount ?? 0) > 0) return "warn";
    }
    if (name === "validate_bundle" && Array.isArray(r.issues)) {
        const errs = (r.issues as { severity?: string }[]).filter((i) => i.severity === "error");
        if (errs.length > 0) return "error";
        if ((r.issues as unknown[]).length > 0) return "warn";
    }
    return "ok";
}

function StructuredSummary({ name, result }: { name: string | null; result: unknown }): React.ReactNode {
    if (!result || typeof result !== "object") return null;
    const r = result as Record<string, unknown>;

    if (r.error) return <Chip color="error" icon={<AlertCircle className="h-3 w-3" />}>{String(r.error)}</Chip>;

    if (name === "render_page" && r.summary) {
        const s = r.summary as Record<string, number>;
        const errs = (s.consoleErrorCount ?? 0) + (s.pageErrorCount ?? 0) + (s.brokenAssetCount ?? 0);
        if (errs > 0) {
            return (
                <>
                    {s.consoleErrorCount ? <Chip color="error">{s.consoleErrorCount} console</Chip> : null}
                    {s.pageErrorCount ? <Chip color="error">{s.pageErrorCount} JS</Chip> : null}
                    {s.brokenAssetCount ? <Chip color="warn">{s.brokenAssetCount} assets</Chip> : null}
                </>
            );
        }
        return <Chip color="ok" icon={<CheckCircle2 className="h-3 w-3" />}>0 issues</Chip>;
    }
    if (name === "validate_bundle" && Array.isArray(r.issues)) {
        const issues = r.issues as { severity?: string }[];
        if (issues.length === 0) return <Chip color="ok" icon={<CheckCircle2 className="h-3 w-3" />}>ok</Chip>;
        return <Chip color="warn">{issues.length} issue{issues.length === 1 ? "" : "s"}</Chip>;
    }
    if (name === "list_files" && Array.isArray(r.files)) return <Chip color="ok">{r.files.length} files</Chip>;
    if (name === "list_assets" && Array.isArray(r.assets)) return <Chip color="ok">{r.assets.length} assets</Chip>;
    if ((name === "write_file" || name === "edit_file") && r.path) {
        const tag = name === "edit_file" ? `${r.replacements ?? 0} edits` : `${r.sizeBytes ?? "?"}b`;
        return (
            <>
                <span className="min-w-0 truncate font-mono text-[10px] text-muted-foreground" title={String(r.path)}>{String(r.path)}</span>
                <Chip color="ok">{tag}</Chip>
            </>
        );
    }
    if (name === "read_file" && r.path) {
        return <span className="min-w-0 truncate font-mono text-[10px] text-muted-foreground" title={String(r.path)}>{String(r.path)} · {String(r.sizeBytes)}b</span>;
    }
    if (name === "generate_image" && r.path) {
        return (
            <span className="min-w-0 truncate font-mono text-[10px] text-muted-foreground" title={String(r.path)}>
                <ImageIcon className="-mt-0.5 mr-1 inline h-3 w-3" />
                {String(r.path)} · {String(r.width)}×{String(r.height)}
            </span>
        );
    }
    if (name === "memory_search" && Array.isArray(r.hits)) {
        return <Chip color="ok">{r.hits.length} hits</Chip>;
    }
    return null;
}

function StructuredView({ name, args, result }: { name: string | null; args: unknown; result: unknown }): React.ReactNode {
    const r = (result && typeof result === "object") ? (result as Record<string, unknown>) : null;

    if (name === "render_page" && r) {
        return (
            <div className="min-w-0 max-w-full space-y-2 text-[11px]">
                <ErrorList title="Console errors" entries={r.consoleErrors as { text: string }[]} fmt={(e) => e.text} />
                <ErrorList title="Uncaught exceptions" entries={r.pageErrors as { message: string }[]} fmt={(e) => e.message} />
                <ErrorList title="Broken assets" entries={r.brokenAssets as { url: string; status?: number }[]} fmt={(e) => `${e.url}${e.status ? ` (${e.status})` : ""}`} />
                <ErrorList title="CSP violations" entries={r.cspViolations as { blockedURI: string }[]} fmt={(e) => e.blockedURI} />
                <ErrorList title="Form-contract issues" entries={r.formContractIssues as { kind: string; selector: string }[]} fmt={(e) => `${e.kind}: ${e.selector}`} />
                {r.loadTiming != null && (
                    <div className="truncate text-muted-foreground">
                        Load: {String((r.loadTiming as Record<string, number>).totalMs ?? "?")}ms · LCP: {(() => {
                            const lcp = (r.loadTiming as Record<string, unknown>).lcpMs;
                            return typeof lcp === "number" ? lcp.toFixed(0) : "?";
                        })()}ms
                    </div>
                )}
            </div>
        );
    }
    if (name === "list_files" && r && Array.isArray(r.files)) {
        return (
            <div className="min-w-0 max-w-full space-y-0.5 font-mono text-[11px]">
                {(r.files as { path: string; sizeBytes: number }[]).map((f) => (
                    <div key={f.path} className="flex min-w-0 items-center justify-between gap-2">
                        <span className="min-w-0 truncate" title={f.path}>{f.path}</span>
                        <span className="shrink-0 text-muted-foreground">{f.sizeBytes}b</span>
                    </div>
                ))}
            </div>
        );
    }
    if (name === "validate_bundle" && r && Array.isArray(r.issues)) {
        return (
            <ul className="min-w-0 max-w-full space-y-0.5 text-[11px]">
                {(r.issues as { severity: string; message: string }[]).map((i, idx) => (
                    <li key={idx} className="flex min-w-0 items-start gap-1">
                        {i.severity === "error"
                            ? <AlertCircle className="mt-0.5 h-3 w-3 shrink-0 text-destructive" />
                            : <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-amber-500" />}
                        <span className="min-w-0 break-words">{i.message}</span>
                    </li>
                ))}
            </ul>
        );
    }
    return (
        <div className="min-w-0 max-w-full font-mono text-[10px] leading-relaxed">
            <div className="mb-1 text-muted-foreground">args</div>
            <pre className="mb-2 max-h-64 max-w-full overflow-auto whitespace-pre-wrap break-all rounded bg-background p-1.5" data-testid="tool-args-pre">{JSON.stringify(args, null, 2)}</pre>
            <div className="mb-1 text-muted-foreground">result</div>
            <pre className="max-h-72 max-w-full overflow-auto whitespace-pre-wrap break-all rounded bg-background p-1.5" data-testid="tool-result-pre">{JSON.stringify(result, null, 2)}</pre>
        </div>
    );
}

function ErrorList<T>({ title, entries, fmt }: { title: string; entries: T[] | undefined; fmt: (e: T) => string }): React.ReactNode {
    if (!Array.isArray(entries) || entries.length === 0) return null;
    return (
        <div>
            <div className="mb-0.5 font-medium">{title} ({entries.length})</div>
            <ul className="space-y-0.5 text-muted-foreground">
                {entries.slice(0, 12).map((e, i) => (<li key={i} className="truncate">• {fmt(e)}</li>))}
                {entries.length > 12 && <li className="italic">… {entries.length - 12} more</li>}
            </ul>
        </div>
    );
}

function Chip({ color, icon, children }: { color: "ok" | "warn" | "error"; icon?: React.ReactNode; children: React.ReactNode }): React.ReactNode {
    const cls =
        color === "error" ? "bg-destructive/15 text-destructive" :
        color === "warn" ? "bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-300" :
        "bg-emerald-100 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300";
    return (
        <span className={cn("inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px]", cls)}>
            {icon}
            {children}
        </span>
    );
}

const STARTERS: { label: string; prompt: string }[] = [
    { label: "Lead-gen page", prompt: "Build a clean lead-gen landing page that captures name + email. Hero, two-paragraph value prop, single CTA, dark theme with a green accent. Use the bundle contract — single-step form with data-lp-form." },
    { label: "Quiz funnel", prompt: "Build a 5-step quiz funnel about personal finance. Each step has 4 multiple-choice options. Final step collects name + email and routes to a thanks page. Use multi-step form contract (data-lp-step / data-lp-next / data-lp-submit)." },
    { label: "Coming-soon", prompt: "Build a minimal 'launching soon' page with one big heading, one sentence of teaser copy, and a single email input that captures the lead. Black background, monospace headline." },
    { label: "VSL longform", prompt: "Build a long-form VSL (video sales letter) page. Hero with H1 + sub, then 6–8 sections of body copy alternating left/right blocks, social proof, FAQs, single CTA pinned at the bottom of each section." },
    { label: "Audit my bundle", prompt: "Run validate_bundle and render_page on the current draft and report any issues (broken assets, console errors, missing form attributes). Then fix them." },
];
